import React, { useCallback, useEffect, useState } from 'react';
import { FileDown } from 'lucide-react';
import { toast } from 'sonner';

import { fleetImportApi } from '../../lib/fleetImportApi';
import { messageErreur } from '../../lib/erreurs';
import { Badge } from '../ui/badge';
import { BarrePagination } from '../ui/pagination-bar';
import { Button } from '../ui/button';
import { StatutBadge } from '../ui/StatutBadge';

/** Rapport final, ligne par ligne : créée, ignorée (elle existait déjà) ou refusée, avec la phrase du refus. */

const TAILLE = 50;
const NATURES = { trucks: 'Camions', drivers: 'Chauffeurs', lot_trucks: 'Camions du lot' };

const RapportImport = ({ job, api = fleetImportApi }) => {
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [donnees, setDonnees] = useState({ items: [], total: 0 });
  const resume = job.report_summary || {};
  // Flotte : camions et chauffeurs ; liste d'un lot : les camions du lot seuls.
  const nomNature = (cle) => job.kind_labels?.[cle] || NATURES[cle];
  const natures = Object.keys(resume).filter((cle) => nomNature(cle));

  const charger = useCallback(async () => {
    try {
      setDonnees(await api.reportLines(job.id, { status: statut, page, pageSize: TAILLE }));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de lire le rapport'));
    }
  }, [api, job.id, statut, page]);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    setPage(1);
  }, [statut]);

  const telecharger = async () => {
    try {
      await api.report(job.id);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de télécharger le rapport'));
    }
  };

  const nombrePages = Math.max(1, Math.ceil((donnees.total || 0) / TAILLE));
  const pagination = {
    page,
    nombrePages,
    taille: TAILLE,
    total: donnees.total || 0,
    premiere: (page - 1) * TAILLE + 1,
    derniere: Math.min(page * TAILLE, donnees.total || 0),
    allerA: (cible) => setPage(Math.min(Math.max(1, cible), nombrePages)),
    changerTaille: () => {},
  };

  return (
    <section className="space-y-3" data-testid="fleet-import-report" aria-label="Rapport de l’import">
      <div className="flex flex-wrap items-center gap-2">
        {(natures.length ? natures : ['trucks', 'drivers']).map((cle) => {
          const bloc = resume[cle] || {};
          return (
            <React.Fragment key={cle}>
              <span className="text-sm font-semibold">{nomNature(cle)}</span>
              <Badge variant="success">{bloc.created || 0} créés</Badge>
              <Badge variant="muted">{bloc.skipped || 0} ignorés</Badge>
              <Badge variant={bloc.error ? 'destructive' : 'muted'}>{bloc.error || 0} refusés</Badge>
            </React.Fragment>
          );
        })}
        <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={telecharger} data-testid="fleet-import-download">
          <FileDown className="h-4 w-4" aria-hidden="true" />
          Télécharger le rapport
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {[
          { cle: '', titre: 'Toutes' },
          { cle: 'created', titre: 'Créées' },
          { cle: 'skipped', titre: 'Ignorées' },
          { cle: 'error', titre: 'Refusées' },
        ].map((filtre) => (
          <Button
            key={filtre.cle || 'toutes'}
            type="button"
            size="sm"
            variant={statut === filtre.cle ? 'secondary' : 'ghost'}
            aria-pressed={statut === filtre.cle}
            onClick={() => setStatut(filtre.cle)}
          >
            {filtre.titre}
          </Button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
        <table className="w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground">
          <thead>
            <tr>
              <th className="text-left">Feuille</th>
              <th className="text-right">Ligne</th>
              <th className="text-left">Élément</th>
              <th className="text-left">Résultat</th>
              <th className="text-left">Précision</th>
            </tr>
          </thead>
          <tbody>
            {donnees.items.map((ligne) => (
              <tr key={ligne.key} className={ligne.status === 'error' ? 'bg-destructive/5' : undefined}>
                <td>{ligne.sheet}</td>
                <td className="text-right tabular-nums">{ligne.row}</td>
                <td className="font-medium">{ligne.label || '—'}</td>
                <td>
                  <StatutBadge domaine="station_reprise" code={ligne.status} />
                </td>
                <td>{ligne.message || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BarrePagination pagination={pagination} nom="lignes" />
    </section>
  );
};

export default RapportImport;
